import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import BackButton from "../../GlobalManagement/Pages/BackButton";
import Sidebar from "../../GlobalManagement/Pages/Sidebar";
import MobileHeaderToggle from "../../GlobalManagement/Pages/MobileHeaderToggle";
import SearchBar from "../../GlobalManagement/Pages/SearchBar";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { Building2, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { 
  masterBodyArea, 
  masterContentArea, 
  masterPageShell, 
  masterHeader, 
  masterHeaderCard, 
  masterHeaderInfo, 
  masterHeaderIcon, 
  masterTitle, 
  masterTableBody, 
  masterPanel, 
  masterTable, 
  masterStickyHeadRow, 
  masterTableData, 
  masterTbody, 
  masterTableRow, 
  masterTableCell 
} from '../../GlobalManagement/Pages/masterPageStyles';

const normalizeEmployeeId = (value) => String(value || "").trim().toUpperCase();
const isExEmployeeId = (value) => normalizeEmployeeId(value).startsWith("EX-");
const getEmployeePrefix = (value) => {
  const normalized = normalizeEmployeeId(value);
  if (!normalized || isExEmployeeId(normalized)) return "";
  return normalized.split("-")[0] || "";
};

const GenerateAllEmployeePayslip = () => {
  const navigate = useNavigate();
  const [selectedMonthYear, setSelectedMonthYear] = useState(localStorage.getItem("payslip_date") || "");
  const [selectedDept, setSelectedDept] = useState(localStorage.getItem("payslip_dept") || "All");
  const [selectedEmployeePrefix, setSelectedEmployeePrefix] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(18);
  
  const [employeesData, setEmployeesData] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const [isGenerated, setIsGenerated] = useState(false); 
  const [isLocked, setIsLocked] = useState(false);
  const [masterLocked, setMasterLocked] = useState(false);
  const tableContainerRef = useRef(null);

  const getPeriod = () => {
    if (!selectedMonthYear) return { month: "", year: "" };
    const [y, m] = selectedMonthYear.split("-");
    const date = new Date(y, m - 1);
    return {
      month: date.toLocaleString('default', { month: 'long' }),
      year: y,
      monthNum: parseInt(m)
    };
  };

  const { month, year, monthNum } = getPeriod();
  const uniqueEarnings = ["Basic", "HRA", "Mobile Allowance", "Bonus", "Management Allowance"];
  const uniqueDeductions = ["PF", "PT", "ESI"];
  
  const normalizeSearchInput = (value) => {
    let v = String(value || "").toUpperCase();
    if (/^[A-Z]+\d/.test(v)) {
      v = v.replace(/^([A-Z]+)-?(\d.*)$/, "$1-$2");
    }
    return v;
  };

  useEffect(() => {
    axios.get("/api/departments")
      .then(res => setDepartments(res.data))
      .catch(() => toast.error("Dept load failed"));
  }, []);

  const runPayFormula = (grossInput) => {
    const S = Math.round(parseFloat(grossInput) || 0);
    
    const basic = Math.round(S * 0.50);
    const hra = Math.round(basic * 0.40);
    const mobile = S > 0 ? 500 : 0; 
    const bonus = Math.round(basic / 6);
    const managementAllowance = S > 0 ? Math.round(S - (basic + hra + mobile + bonus)) : 0;

    const pf = Math.round(basic * 0.12);
    const pt = Math.round(S > 25000 ? 208 : (S >= 15000 ? 180 : 0));
    const esi = Math.round(S * 0.0075);

    return {
      gross: S,
      earnings: [
        { headName: "Basic", value: basic },
        { headName: "HRA", value: hra },
        { headName: "Mobile Allowance", value: mobile },
        { headName: "Bonus", value: bonus },
        { headName: "Management Allowance", value: managementAllowance },
      ],
      deductions: [
        { headName: "PF", value: pf },
        { headName: "PT", value: pt },
        { headName: "ESI", value: esi },
      ],
      totalDeduction: pf + pt + esi
    };
  };

  const checkStatus = async (m, y, dept) => {
    if (!m || !y) return null;
    try {
      const masterRes = await axios.get(`/api/payslips/check-batch?month=${m}&year=${y}&filterDept=All`);
      const masterData = masterRes.data.data;
      setMasterLocked(masterData?.status === "Finalized");

      const res = await axios.get(`/api/payslips/check-batch?month=${m}&year=${y}&filterDept=${dept}`);
      if (res.data.exists) {
        setIsGenerated(true);
        setIsLocked(res.data.data.status === "Finalized");
        return res.data.data.employeePayslips;
      }
      setIsGenerated(false);
      setIsLocked(false);
      return null;
    } catch (err) { return null; }
  };

  useEffect(() => {
    const fetchAndCalculate = async () => {
      if (!selectedMonthYear) return;
      localStorage.setItem("payslip_date", selectedMonthYear);
      localStorage.setItem("payslip_dept", selectedDept);

      try {
        setLoading(true);

        const [
          existingDataRes,
          empRes,
          attRes,
          otRatesRes
        ] = await Promise.allSettled([
          checkStatus(month, year, selectedDept),
          axios.get("/api/employees"),
          axios.get(`/api/attendance/history?month=${monthNum}&year=${year}&summary=1&skipSync=1`),
          axios.get("/api/ot/ot-rate/rule")
        ]);

        const existingData = existingDataRes.status === "fulfilled" ? existingDataRes.value : null;
        const employeesPayload = empRes.status === "fulfilled" ? empRes.value : null;
        const attendancePayload = attRes.status === "fulfilled" ? attRes.value : null;
        const otRatesPayload = otRatesRes.status === "fulfilled" ? otRatesRes.value : null;

        if (!employeesPayload) {
          const msg = empRes?.reason?.response?.data?.message || empRes?.reason?.message || "Employee fetch failed";
          toast.error(`Employee load failed: ${msg}`);
          setLoading(false);
          return;
        }

        const mDays = new Date(year, monthNum, 0).getDate();
        const allOtRules = otRatesPayload?.data?.data || otRatesPayload?.data || [];

        const listWithMath = (employeesPayload.data || [])
        .filter((emp) => !isExEmployeeId(emp.employeeID))
        .map((emp) => {
          const att = attendancePayload?.data?.find(a => a.employeeUserId === emp.employeeUserId) || {};
          const saved = existingData?.find(s => s.employeeId === emp.employeeID);
          
          const otRule = allOtRules.find(r => r.employeeUserId === emp.employeeUserId);
          const otRate = otRule?.otRatePerHour || 0;

          const currentGross = saved ? saved.grossSalary : (emp.grossSalary || "");
          const rawEarnings = saved ? saved.earnings : (emp.earnings || []);
          const rawDeductions = saved ? saved.deductions : (emp.deductions || []);

          const normalizedEarnings = rawEarnings.map(e => ({
            ...e,
            value: e.amount !== undefined ? e.amount : (e.value !== undefined ? e.value : "")
          }));

          const normalizedDeductions = rawDeductions.map(d => ({
            ...d,
            value: d.amount !== undefined ? d.amount : (d.value !== undefined ? d.value : "")
          }));
          
          const workingDays = Number(att.totalPresent || 0);
          const offDays = Number(att.totalOff || 0);
          const leaveDays = Number(att.totalLeave || 0);
          const absentDays = Number(att.totalAbsent || 0);
          const totalPaidDays = Number(att.totalPaidDays || 0);
          
          const otHours = saved ? saved.otHours : "";
          const otAmount = saved ? saved.otAmount : (otHours !== "" ? Math.round(otHours * otRate) : "");
          
          const totalDedSum = normalizedDeductions.reduce((sum, d) => sum + (Number(d.value) || 0), 0);
          const paidDaysSalary = mDays > 0 ? Math.round(((Number(currentGross || 0) - totalDedSum) / mDays) * totalPaidDays) : 0;

          return {
            ...emp,
            otRate,
            earnings: normalizedEarnings,
            deductions: normalizedDeductions,
            calc: {
              monthDays: mDays,
              grossSalary: currentGross,
              totalDeductionHeads: totalDedSum,
              paidDaysSalary,
              otHours, 
              otAmount, 
              netSalary: Math.round(paidDaysSalary + Number(otAmount || 0)),
              totalWorkingDays: workingDays, 
              totalOff: offDays,
              leaves: leaveDays,
              absentDays: absentDays,
              totalPaidDays: totalPaidDays
            }
          };
        });

        setEmployeesData(listWithMath);
        setLoading(false);
      } catch (err) { 
        toast.error("Fetch Error:", err);
        setLoading(false); 
      }
    };

    fetchAndCalculate();
  }, [selectedMonthYear, selectedDept]);

  const employeePrefixOptions = ["ALL", ...new Set(
    employeesData.map((emp) => getEmployeePrefix(emp.employeeID)).filter(Boolean)
  )];

  const handleUniversalEdit = (employeeID, field, value, headName = null) => {
    if (isLocked || masterLocked) return;
    
    const updated = [...employeesData];
    const emp = updated.find(e => e.employeeID === employeeID);
    if (!emp) return;

    if (field === "grossSalary") {
      const struct = runPayFormula(value);
      emp.calc.grossSalary = value === "" ? "" : struct.gross;
      emp.earnings = struct.earnings;
      emp.deductions = struct.deductions;
    } 
    else if (field === "earning") {
        const target = emp.earnings.find(e => e.headName === headName);
        if (target) target.value = value === "" ? "" : Number(value);
    }
    else if (field === "deduction") {
        const target = emp.deductions.find(d => d.headName === headName);
        if (target) target.value = value === "" ? "" : Number(value);
    }
    else if (field === "otHours") {
      emp.calc.otHours = value;
      emp.calc.otAmount = value === "" ? "" : Math.round(Number(value) * emp.otRate);
    } 
    else if (field === "otAmount") {
      emp.calc.otAmount = value === "" ? "" : Number(value);
    }

    const totalDeductionHeads = emp.deductions.reduce((sum, d) => sum + Number(d.value || 0), 0);
    emp.calc.paidDaysSalary = Math.round(((Number(emp.calc.grossSalary || 0) - totalDeductionHeads) / emp.calc.monthDays) * emp.calc.totalPaidDays);
    emp.calc.netSalary = Math.round(emp.calc.paidDaysSalary + Number(emp.calc.otAmount || 0));
    
    setEmployeesData(updated);
  };

  const filteredEmployees = employeesData.filter((e) => {
    const deptMatch = selectedDept === "All" || e.departmentName === selectedDept;
    const prefixMatch =
      selectedEmployeePrefix === "ALL" ||
      getEmployeePrefix(e.employeeID) === selectedEmployeePrefix;
    if (!deptMatch || !prefixMatch || isExEmployeeId(e.employeeID)) return false;
    const q = String(searchTerm || "").toUpperCase().trim();
    if (!q) return true;
    const name = `${e.firstName || ""} ${e.lastName || ""}`.trim().toUpperCase();
    const employeeId = String(e.employeeID || "").toUpperCase();
    const employeeUserId = String(e.employeeUserId || "").toUpperCase();
    return name.includes(q) || employeeId.includes(q) || employeeUserId.includes(q);
  });

  const paginatedEmployees = pageSize === -1 
    ? filteredEmployees 
    : filteredEmployees.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const scrollTable = (direction) => {
    if (tableContainerRef.current) {
      const scrollAmount = 400;
      tableContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const handleBulkSubmit = async (status = "Draft") => {
    setIsProcessing(true);
    const tid = toast.loading(`Saving ${selectedDept} for ${month} ${year}...`);
    try {
      const payload = {
        month, 
        year, 
        status, 
        filterDept: selectedDept,
        employeePayslips: filteredEmployees
        .filter((emp) => !isExEmployeeId(emp.employeeID))
        .map(emp => ({
          employeeId: emp.employeeID,
          employeeUserId: emp.employeeUserId,
          employeeName: `${emp.firstName} ${emp.lastName}`.trim(),
          
          departmentName: emp.departmentName || "",
          designationName: emp.designationName || "",
          doj: emp.doj || "",
          mobile: emp.permanentAddress?.mobile || emp.presentAddress?.mobile || "",
          email: emp.permanentAddress?.email || emp.presentAddress?.email || "",
          
          earnings: emp.earnings.map(e => ({
            headName: e.headName,
            type: e.type || "Fixed", 
            amount: e.value === "" || e.value === undefined ? 0 : Number(e.value)
          })),
          
          deductions: emp.deductions.map(d => ({
            headName: d.headName,
            type: d.type || "Fixed",
            amount: d.value === "" || d.value === undefined ? 0 : Number(d.value)
          })),

          grossSalary: Number(emp.calc.grossSalary || 0),
          otHours: Number(emp.calc.otHours || 0),
          otAmount: Number(emp.calc.otAmount || 0),
          totalPaidDays: Number(emp.calc.totalPaidDays || 0),
          netSalary: Number(emp.calc.netSalary || 0),
          inHandSalary: Number(emp.calc.netSalary || 0),
          
          lopDays: Number(emp.calc.absentDays || 0),
          lopAmount: 0,
          monthDays: Number(emp.calc.monthDays || 0),
          totalWorkingDays: Number(emp.calc.totalWorkingDays || 0),
          totalOff: Number(emp.calc.totalOff || 0),
          leaves: Number(emp.calc.leaves || 0),
          
          totalEarnings: emp.earnings.reduce((sum, e) => sum + (Number(e.value) || 0), 0),
          totalDeduction: emp.deductions.reduce((sum, d) => sum + (Number(d.value) || 0), 0),
        }))
      };

      const res = await axios.post("/api/payslips/bulk", payload);
      toast.success(res.data.message, { id: tid });
      
      setIsGenerated(true);
      if (status === "Finalized") setIsLocked(true);
      
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) { 
        const backendMsg = err?.response?.data?.message || "";
        if (/already|exists|duplicate/i.test(backendMsg)) {
          toast.error(backendMsg, { id: tid });
        } else {
          toast.dismiss(tid);
          toast.error("Payslip batch save error:", err);
        }
    } finally { setIsProcessing(false); }
  };

  const clearMonth = async () => {
    if (!window.confirm(`Clear all history for ${selectedDept} - ${month} ${year}?`)) return;
    try {
      const res = await axios.delete(`/api/payslips/clean?month=${month}&year=${year}&filterDept=${selectedDept}`);
      if (res.data.success) {
        toast.success(res.data.message);
        setIsGenerated(false);
        setIsLocked(false);
        setTimeout(() => window.location.reload(), 1500);
      }
    } catch (err) {
      const backendMsg = err?.response?.data?.message || "";
      if (/already|exists|duplicate/i.test(backendMsg)) {
        toast.error(backendMsg);
      } else {
        toast.error("Clear month error:", err);
      }
    }
  };

  const exportExcel = () => {
    const headerRow1 = ["Employee Info", "", "", "", "Earnings", "", "", "", "", "", "Deductions", "", "", "Overtime", "", "Summary", ""];
    const headerRow2 = ["SL", "ID", "Name", "Dept", "Gross", "Basic", "HRA", "Mobile Allowance", "Bonus", "Management Allowance", "PF", "PT", "ESI", "OT Hrs", "OT Amt", "Paid Days", "Net Pay"];

    const dataRows = filteredEmployees.map((emp, idx) => {
      const getVal = (arr, head) => {
        const found = arr.find(h => h.headName === head);
        return found ? (found.amount !== undefined ? found.amount : found.value) : 0;
      };
      return [
        idx + 1, emp.employeeID, `${emp.firstName} ${emp.lastName}`, emp.departmentName, emp.calc.grossSalary,
        getVal(emp.earnings, "Basic"), getVal(emp.earnings, "HRA"), getVal(emp.earnings, "Mobile Allowance"), getVal(emp.earnings, "Bonus"), getVal(emp.earnings, "Management Allowance"),
        getVal(emp.deductions, "PF"), getVal(emp.deductions, "PT"), getVal(emp.deductions, "ESI"),
        emp.calc.otHours || 0, emp.calc.otAmount || 0, emp.calc.totalPaidDays, emp.calc.netSalary
      ];
    });

    const worksheet = XLSX.utils.aoa_to_sheet([headerRow1, headerRow2, ...dataRows]);

    const baseStyle = { font: { bold: true, color: { rgb: "FFFFFF" } }, alignment: { horizontal: "center" }, fill: { fgColor: { rgb: "334155" } } };
    const empStyle = { ...baseStyle, fill: { fgColor: { rgb: "475569" } } };
    const earnStyle = { ...baseStyle, fill: { fgColor: { rgb: "1E3A8A" } } };
    const dedStyle = { ...baseStyle, fill: { fgColor: { rgb: "991B1B" } } };
    const otStyle = { ...baseStyle, fill: { fgColor: { rgb: "C2410C" } } };
    const sumStyle = { ...baseStyle, fill: { fgColor: { rgb: "15803D" } } };

    const range1 = [
      { c: 0, style: empStyle }, { c: 4, style: earnStyle }, 
      { c: 10, style: dedStyle }, { c: 13, style: otStyle }, { c: 15, style: sumStyle }
    ];

    range1.forEach(item => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: item.c });
      if (worksheet[cellRef]) worksheet[cellRef].s = item.style;
    });

    const subheadStyle = { font: { bold: true }, fill: { fgColor: { rgb: "F1F5F9" } }, border: { bottom: { style: "thin" } } };
    for (let c = 0; c <= 16; c++) {
      const cellRef = XLSX.utils.encode_cell({ r: 1, c: c });
      if (worksheet[cellRef]) worksheet[cellRef].s = subheadStyle;
    }

    worksheet["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 3 } },
      { s: { r: 0, c: 4 }, e: { r: 0, c: 9 } },
      { s: { r: 0, c: 10 }, e: { r: 0, c: 12 } },
      { s: { r: 0, c: 13 }, e: { r: 0, c: 14 } },
      { s: { r: 0, c: 15 }, e: { r: 0, c: 16 } }
    ];

    worksheet["!cols"] = [{ wch: 5 }, { wch: 12 }, { wch: 25 }, { wch: 15 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 12 }];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Payroll_Data");
    XLSX.writeFile(workbook, `Payroll_${selectedDept}_${month}_${year}.xlsx`);
  };

  return (
    <div className={masterBodyArea}>
      <Sidebar />
      <div className={`${masterPageShell} ${masterContentArea}`}>
        
        {/* Header Section */}
        <header className={masterHeader}>
          <div className={masterHeaderCard}>
            <div className={masterHeaderInfo}>
              <div className={masterHeaderIcon}><Building2 size={14} /></div>
              <div><h1 className={masterTitle}>Payslip Control</h1></div>
            </div>
            <div className="flex items-center gap-2">
              <BackButton />
            </div>
          </div>
        </header>

        <main className={`${masterTableBody} mt-1 w-full flex-1 flex flex-col min-h-0`}>
          <MobileHeaderToggle>
            <div className={`${masterPanel} p-3 mb-3 border border-slate-200 bg-white`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
                <div className="flex flex-col">
                  <label className="font-semibold text-slate-700 text-[11px] uppercase mb-1">Period</label>
                  <input 
                    type="month" 
                    className="border border-slate-300 rounded px-2.5 py-1 bg-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 font-bold" 
                    value={selectedMonthYear} 
                    onChange={(e) => setSelectedMonthYear(e.target.value)} 
                  />
                </div>

                <div className="flex flex-col">
                  <label className="font-semibold text-slate-700 text-[11px] uppercase mb-1">Status</label>
                  <select 
                    className="border border-slate-300 rounded px-2.5 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 font-bold" 
                    value={selectedEmployeePrefix} 
                    onChange={(e) => setSelectedEmployeePrefix(e.target.value)}
                  >
                    {employeePrefixOptions.map((prefix) => <option key={prefix} value={prefix}>{prefix}</option>)}
                  </select>
                </div>

                <div className="flex flex-col">
                  <label className="font-semibold text-slate-700 text-[11px] uppercase mb-1">Filter Dept</label>
                  <select 
                    className="border border-slate-300 rounded px-2.5 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 font-bold" 
                    value={selectedDept} 
                    onChange={(e) => setSelectedDept(e.target.value)}
                  >
                    <option value="All">All Departments</option>
                    {departments.map(d => <option key={d._id} value={d.deptName}>{d.deptName}</option>)}
                  </select>
                </div>

                <div className="flex flex-wrap gap-2 items-end">
                  <button onClick={exportExcel} className="flex-1 whitespace-nowrap bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded font-bold text-[10px] uppercase shadow transition-all">Excel</button>
                  <button onClick={() => handleBulkSubmit("Draft")} disabled={masterLocked || isLocked || isProcessing || isGenerated} className="flex-1 whitespace-nowrap bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded font-bold text-[10px] uppercase disabled:bg-slate-300 shadow transition-all">Generate</button>
                  <button onClick={clearMonth} disabled={masterLocked || isLocked || !isGenerated} className="flex-1 whitespace-nowrap bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded font-bold text-[10px] uppercase disabled:bg-slate-300 shadow transition-all">Clear</button>
                  <button onClick={() => handleBulkSubmit("Finalized")} disabled={masterLocked || isLocked || !isGenerated || isProcessing} className="flex-1 whitespace-nowrap bg-red-700 hover:bg-red-800 text-white px-3 py-1.5 rounded font-bold text-[10px] uppercase disabled:bg-slate-300 shadow transition-all">Close</button>
                </div>
              </div>
            </div>
          </MobileHeaderToggle>

          <div className={`${masterPanel} flex flex-col overflow-hidden w-full flex-1 relative`}>
            {/* SearchBar with Integrated Pagination */}
            <div className="border-b border-slate-200 flex-shrink-0 bg-white p-1 sm:p-2 z-20">
              <SearchBar 
                value={searchTerm} 
                onChange={(val) => {
                  setSearchTerm(normalizeSearchInput(val));
                  setCurrentPage(1);
                }} 
                placeholder="Search name / user ID / emp ID..." 
                totalItems={filteredEmployees.length} 
                currentPage={currentPage} 
                pageSize={pageSize} 
                onPageChange={setCurrentPage} 
                onPageSizeChange={setPageSize} 
              />
            </div>

            {/* Horizontal Scroll Arrows */}
            <button 
              type="button"
              onClick={() => scrollTable('left')}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-50 bg-white/90 hover:bg-white text-green-600 shadow-md border border-slate-200 p-2 rounded-full transition-all active:scale-90 hidden md:flex items-center justify-center"
              title="Scroll Left"
            >
              <ChevronLeft size={20} />
            </button>

            <button 
              type="button"
              onClick={() => scrollTable('right')}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-50 bg-white/90 hover:bg-white text-green-600 shadow-md border border-slate-200 p-2 rounded-full transition-all active:scale-90 hidden md:flex items-center justify-center"
              title="Scroll Right"
            >
              <ChevronRight size={20} />
            </button>

            <div 
              ref={tableContainerRef} 
              className="w-full flex-1 min-h-0 overflow-auto bg-white rounded-lg shadow border border-slate-200 scroll-smooth relative"
            >
              <table className={`${masterTable} min-w-[1500px]`}>
                <thead>
                  <tr className="bg-slate-800 text-white text-[10px] uppercase sticky top-0 z-30">
                    <th className="p-2 border border-slate-700" colSpan="4">Employee Info</th>
                    <th className="p-2 border border-slate-700 bg-blue-900" colSpan={uniqueEarnings.length + 1}>Earnings</th>
                    <th className="p-2 border border-slate-700 bg-red-900" colSpan={uniqueDeductions.length}>Deductions</th>
                    <th className="p-2 border border-slate-700 bg-orange-700" colSpan="2">Overtime</th>
                    <th className="p-2 bg-slate-900" colSpan="2">Summary</th>
                  </tr>
                  <tr className={`${masterStickyHeadRow} top-[29px]`}>
                    <th className={masterTableData}>SL</th>
                    <th className={masterTableData}>ID</th>
                    <th className={masterTableData} style={{ minWidth: '150px' }}>Name</th>
                    <th className={`${masterTableData} bg-yellow-50`}>Dept</th>
                    <th className={`${masterTableData} bg-blue-600 text-white font-black italic`}>Gross</th>
                    {uniqueEarnings.map(h => <th key={h} className={`${masterTableData} bg-blue-50 text-slate-800`}>{h}</th>)}
                    {uniqueDeductions.map(h => <th key={h} className={`${masterTableData} bg-red-50 text-slate-800`}>{h}</th>)}
                    <th className={`${masterTableData} bg-orange-50 text-slate-800`}>OT Hrs</th>
                    <th className={`${masterTableData} bg-orange-50 text-slate-800`}>OT Amt</th>
                    <th className={`${masterTableData} bg-slate-200 text-slate-800`}>Paid Days</th>
                    <th className={`${masterTableData} bg-green-700 text-white font-bold`}>Net Pay</th>
                  </tr>
                </thead>
                <tbody className={masterTbody}>
                  {loading ? (
                    <tr>
                      <td colSpan={20} className="p-16 text-center bg-white">
                        <div className="flex flex-col items-center justify-center gap-2">
                          <Loader2 className="animate-spin text-slate-600" size={24} />
                          <span className="text-xs font-semibold text-slate-600">
                            Loading Employee Payslip Data... Please wait...
                          </span>
                        </div>
                      </td>
                    </tr>
                  ) : paginatedEmployees.length > 0 ? (
                    paginatedEmployees.map((emp, idx) => {
                      const absoluteIndex = pageSize === -1 ? idx + 1 : (currentPage - 1) * pageSize + idx + 1;
                      return (
                        <tr key={emp.employeeID} className={`${masterTableRow} ${(isLocked || masterLocked) ? 'bg-gray-100' : (idx % 2 === 0 ? "bg-slate-50/50" : "bg-white")}`}>
                          <td className={`${masterTableCell} text-center`}>{absoluteIndex}</td>
                          <td className={`${masterTableCell} font-bold text-slate-600`}>{emp.employeeID}</td>
                          <td className={`${masterTableCell} text-left uppercase text-gray-900 font-semibold`}>
                            {[emp.firstName, emp.middleName, emp.lastName].filter(Boolean).join(" ")}
                          </td>
                          <td className={`${masterTableCell} bg-yellow-50/20 font-semibold`}>{emp.departmentName}</td>
                          
                          {/* Gross Salary */}
                          <td className={`${masterTableCell} bg-blue-50/50 text-center`}>
                            <input 
                              type="number" 
                              disabled={isLocked || masterLocked} 
                              className="w-20 text-center font-black bg-white border border-slate-300 rounded py-0.5 text-xs" 
                              value={emp.calc.grossSalary === 0 || emp.calc.grossSalary === null ? "" : emp.calc.grossSalary} 
                              onChange={(e) => handleUniversalEdit(emp.employeeID, 'grossSalary', e.target.value)} 
                            />
                          </td>

                          {/* Earnings */}
                          {uniqueEarnings.map(h => (
                            <td key={h} className={`${masterTableCell} text-center`}>
                              <input 
                                type="number" 
                                disabled={isLocked || masterLocked} 
                                className="w-16 text-center bg-transparent text-blue-700 font-bold text-xs" 
                                value={emp.earnings.find(e => e.headName === h)?.value || ""} 
                                onChange={(e) => handleUniversalEdit(emp.employeeID, 'earning', e.target.value, h)} 
                              />
                            </td>
                          ))}

                          {/* Deductions */}
                          {uniqueDeductions.map(h => (
                            <td key={h} className={`${masterTableCell} text-center`}>
                              <input 
                                type="number" 
                                disabled={isLocked || masterLocked} 
                                className="w-16 text-center bg-transparent text-red-600 font-bold text-xs" 
                                value={emp.deductions.find(d => d.headName === h)?.value || ""} 
                                onChange={(x) => handleUniversalEdit(emp.employeeID, 'deduction', x.target.value, h)} 
                              />
                            </td>
                          ))}

                          {/* Overtime */}
                          <td className={`${masterTableCell} bg-orange-50/50 text-center`}>
                            <input 
                              type="number" 
                              disabled={isLocked || masterLocked} 
                              className="w-12 text-center bg-white border border-slate-300 rounded font-semibold text-xs py-0.5" 
                              value={emp.calc.otHours} 
                              onChange={(e) => handleUniversalEdit(emp.employeeID, 'otHours', e.target.value)} 
                            />
                          </td>
                          <td className={`${masterTableCell} bg-orange-50/50 text-center`}>
                            <input 
                              type="number" 
                              disabled={isLocked || masterLocked} 
                              className="w-16 text-center bg-white border border-slate-300 rounded font-semibold italic text-xs py-0.5" 
                              value={emp.calc.otAmount} 
                              onChange={(e) => handleUniversalEdit(emp.employeeID, 'otAmount', e.target.value)} 
                            />
                          </td>

                          <td className={`${masterTableCell} bg-slate-100 font-bold italic text-center`}>{emp.calc.totalPaidDays}</td>
                          <td className={`${masterTableCell} bg-green-50 text-green-900 font-black text-xs text-center`}>₹{emp.calc.netSalary}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="20" className="p-12 text-center text-slate-400 font-bold uppercase text-xs">
                        No employee payslip records found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default GenerateAllEmployeePayslip;